import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { PDKMessageIcon } from '@/components/ui/pdk-icons';
import { usePostHog } from '@/contexts/posthog-context';
import {
  FEATURE_FLAG_NAMES,
  useIsFeatureEnabled,
} from '@/hooks/use-feature-flags';
import { POSTHOG_EVENTS } from '@/lib/posthog-events';
import dayjs from 'dayjs';
import { isEmpty } from 'lodash';
import { useEffect, useRef } from 'react';
import { parseSMSMessage } from '../parse-sms-message';

const TextConvo = ({
  smsConversation,
  dealShortId,
  isFromTextRecap = false,
}: {
  smsConversation: string[] | null;
  dealShortId: string;
  isFromTextRecap?: boolean;
}) => {
  const { isFeatureEnabled } = useIsFeatureEnabled({
    featureFlagName: FEATURE_FLAG_NAMES.TEXT_CONVO,
  });

  const posthog = usePostHog();
  const buttonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (isFeatureEnabled && buttonRef.current && smsConversation) {
      posthog?.capture(POSTHOG_EVENTS.TEXT_CONVO.BUTTON_SHOWN, {
        deal_short_id: dealShortId,
      });
    }
  }, [
    isFeatureEnabled,
    dealShortId,
    smsConversation,
    posthog,
    buttonRef.current,
  ]);

  const handleTextConvoClick = () => {
    const eventName = isFromTextRecap
      ? POSTHOG_EVENTS.TEXT_RECAP.TEXT_CONVO_BUTTON_CLICKED
      : POSTHOG_EVENTS.TEXT_CONVO.BUTTON_CLICKED;

    posthog?.capture(eventName, {
      deal_short_id: dealShortId,
      source: isFromTextRecap ? 'text_recap' : 'header',
    });
  };

  if (!isFeatureEnabled || isEmpty(smsConversation)) {
    return null;
  }

  return (
    <Dialog modal={false}>
      <DialogTitle className="sr-only">Text Conversation</DialogTitle>
      <DialogTrigger asChild>
        <DialogHeader>
          <Button
            ref={buttonRef}
            variant="outline"
            size="sm"
            onClick={handleTextConvoClick}
          >
            <PDKMessageIcon />
            Text Convo
          </Button>
        </DialogHeader>
      </DialogTrigger>

      <DialogContent
        className="fixed right-0 left-auto top-0 h-screen max-w-[96vw] sm:max-w-96 translate-x-0 translate-y-0 rounded-none border-l flex flex-col gap-4"
        onClickClose={() => {}}
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogDescription className="sr-only">
          Text Conversation displays the full SMS conversation history.
        </DialogDescription>
        <div className="text-sm  flex flex-row items-center gap-2 h-fit">
          <PDKMessageIcon className="h-4 w-4 text-blue-500" />
          Text Conversation
        </div>

        <div className="flex-1 overflow-y-auto space-y-3 px-2">
          {smsConversation &&
            smsConversation.map((item, index) => {
              const parsed = parseSMSMessage(item);
              const formattedTime = parsed.timestamp
                ? dayjs.utc(parsed.timestamp).local().format('MMM D, h:mm A')
                : null;

              return (
                <div
                  key={index}
                  className={`flex ${parsed.isCustomer ? 'justify-start' : 'justify-end'}`}
                >
                  <div
                    className={`max-w-[85%] ${parsed.isCustomer ? 'order-1' : 'order-2'}`}
                  >
                    <div
                      className={`rounded-2xl px-3 py-2 shadow-sm ${
                        parsed.isCustomer
                          ? 'bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-gray-100'
                          : 'bg-blue-500 text-white'
                      }`}
                    >
                      <div className="text-sm leading-relaxed break-words whitespace-pre-wrap">
                        {parsed.content}
                      </div>
                    </div>
                    <div
                      className={`text-xs text-gray-500 dark:text-gray-400 mt-1 px-2 ${
                        parsed.isCustomer ? 'text-left' : 'text-right'
                      }`}
                    >
                      {parsed.author} • {formattedTime}
                    </div>
                  </div>
                </div>
              );
            })}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default TextConvo;
